import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Grid,
  IconButton,
  Tooltip
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { setToast } from '../../../../components/Toasts/data-access/toast-reducer';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudDownload, faGlobe, faUser, faCalendar, faFolder } from '@fortawesome/free-solid-svg-icons';
import logo from '../../../../assets/img/icons/logo-light-multi.png';

interface AuditReportResponse {
  audit_id: string;
  project_id: string;
  project_name: string;
  environment: string;
  audit_timestamp_local: string;
  audit_timestamp_utc: string;
  reviewer: {
    name: string;
    verdict: string;
    comments: string;
  };
  scope_of_analysis: {
    ml_units_evaluated: number;
    llm_units_evaluated: number;
  };
  kpis_dimension_level: {
    ethical_behavior_score: number;
    ai_safety_risk_pct: number;
    governance_coverage_pct: number;
    internal_violations_count: number;
    external_violations_count: number;
  };
  controls: {
    owasp_controls: Array<{
      name: string;
      status: string;
      severity: string;
      category: string;
      reference: string;
    }>;
    ontology_signals: Array<{
      domain?: string;
      risk_domain?: string;
      risk_level: string;
    }>;
    internal_compliance: Array<{
      dimension: string;
      issue_type: string;
      severity: string;
      status: string;
      evidence: { [key: string]: string };
      provenance: string;
      internal_clause: string | null;
      reason: string;
    }>;
    external_compliance: Array<{
      dimension: string;
      issue_type: string;
      feature: string;
      evidence: string;
      status: string;
      matches: Array<{
        regulation: string;
        article: string;
        obligation: string;
        distance: number;
        reason: string;
      }>;
    }>;
  };
  risk_and_recommendations_advisory: string;
  final_verdict_reviewer_only: {
    verdict: string;
    rationale_verbatim: string;
  };
  full_text_report: string;
  ocaf_anchor?: {
    ocaf_sha256_hash: string;
    status?: string;
  };
}

interface AuditReportTabProps {
  projectId: string;
  userId?: string;
  initialAuditReport?: AuditReportResponse | null;
  onReportGenerated?: (report: AuditReportResponse | null) => void;
}

const AuditReportTab = ({ projectId, initialAuditReport }: AuditReportTabProps) => {
  const dispatch = useDispatch();
  const reportRef = useRef<HTMLDivElement>(null);
  const [auditReport, setAuditReport] = useState<AuditReportResponse | null>(initialAuditReport || null);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Update local state when initialAuditReport prop changes
  useEffect(() => {
    if (initialAuditReport) {
      setAuditReport(initialAuditReport);
      setError(null);
    }
  }, [initialAuditReport]);

  // Parse ontology signals from full_text_report
  const parseOntologySignalsFromText = (reportText: string) => {
    if (!reportText) return [];

    const signals: Array<{ category: string; riskLevel: string }> = [];
    const lines = reportText.split('\n').map(line => line.trim());

    // Find the "Ontology-Based Governance Signals" or similar section
    let inOntologySection = false;
    let sectionEnded = false;
    let sectionStartIndex = -1;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Detect the start of ontology signals section
      if (
        /ontology.*governance.*signal/i.test(line) ||
        /ontology.*based.*governance/i.test(line) ||
        /3\.\s*ONTOLOGY/i.test(line) ||
        /ONTOLOGY.*BASED.*GOVERNANCE/i.test(line)
      ) {
        inOntologySection = true;
        sectionStartIndex = i;
        continue;
      }

      // Detect end of section (next numbered section or major header)
      if (
        inOntologySection &&
        sectionStartIndex >= 0 &&
        i > sectionStartIndex + 1 &&
        /^\d+\.\s+[A-Z]/.test(line) &&
        !/ontology/i.test(line)
      ) {
        sectionEnded = true;
        break;
      }

      // Skip separator lines
      if (inOntologySection && /^[-=_]+$/.test(line)) {
        continue;
      }

      // Skip empty lines
      if (inOntologySection && line.length === 0) {
        continue;
      }

      // Parse lines in the ontology section
      if (inOntologySection && !sectionEnded && line.length > 0) {
        // Pattern: "- Category Name | Risk Level: LEVEL"
        const match1 = line.match(/^[-•*]?\s*(.+?)\s*\|\s*Risk\s+Level:\s*(LOW|HIGH|MEDIUM|UNKNOWN)/i);
        if (match1) {
          signals.push({
            category: match1[1].trim(),
            riskLevel: match1[2].trim().toUpperCase()
          });
          continue;
        }

        // Pattern: "- Category Name | LEVEL" (without "Risk Level:" label)
        const match2 = line.match(/^[-•*]?\s*(.+?)\s*\|\s*(LOW|HIGH|MEDIUM|UNKNOWN)\s*$/i);
        if (match2) {
          signals.push({
            category: match2[1].trim(),
            riskLevel: match2[2].trim().toUpperCase()
          });
          continue;
        }

        // Pattern: "Category Name: Risk Level: LEVEL"
        const match3 = line.match(/^[-•*]?\s*(.+?):\s*Risk\s+Level:\s*(LOW|HIGH|MEDIUM|UNKNOWN)/i);
        if (match3) {
          signals.push({
            category: match3[1].trim(),
            riskLevel: match3[2].trim().toUpperCase()
          });
          continue;
        }

        // Pattern: "Category Name: LEVEL"
        const match4 = line.match(/^[-•*]?\s*(.+?):\s*(LOW|HIGH|MEDIUM|UNKNOWN)\s*$/i);
        if (match4) {
          signals.push({
            category: match4[1].trim(),
            riskLevel: match4[2].trim().toUpperCase()
          });
          continue;
        }

        // Pattern: Two parts separated by dash (e.g., "Category - LEVEL")
        const match5 = line.match(/^[-•*]?\s*(.+?)\s*-\s*(LOW|HIGH|MEDIUM|UNKNOWN)\s*$/i);
        if (match5) {
          signals.push({
            category: match5[1].trim(),
            riskLevel: match5[2].trim().toUpperCase()
          });
          continue;
        }

        // Pattern: Just risk level on a line (try to get category from previous line or context)
        if (/^(LOW|HIGH|MEDIUM|UNKNOWN)\s*$/i.test(line)) {
          // If we have a previous signal, this might be a continuation
          // Otherwise, skip as we don't have category
          continue;
        }
      }
    }

    return signals;
  };

  // Extract metrics from structured report data
  const extractMetrics = (auditReport: AuditReportResponse) => {
    const metrics: { [key: string]: any } = {};

    // Extract OWASP controls from structured data
    if (auditReport.controls?.owasp_controls) {
      metrics.owasp = auditReport.controls.owasp_controls.map(control => ({
        test: control.name,
        status: control.status,
        severity: control.severity,
        category: control.category,
        reference: control.reference
      }));
    }

    // Extract Risk Levels from full_text_report first, fallback to structured data
    if (auditReport.full_text_report) {
      const parsedSignals = parseOntologySignalsFromText(auditReport.full_text_report);
      if (parsedSignals.length > 0) {
        metrics.riskLevels = parsedSignals;
      } else if (auditReport.controls?.ontology_signals) {
        // Fallback to structured data if parsing fails
        metrics.riskLevels = auditReport.controls.ontology_signals.map(signal => ({
          category: signal.domain || signal.risk_domain || '',
          riskLevel: signal.risk_level || ''
        }));
      }
    } else if (auditReport.controls?.ontology_signals) {
      // Use structured data if full_text_report is not available
      metrics.riskLevels = auditReport.controls.ontology_signals.map(signal => ({
        category: signal.domain || signal.risk_domain || '',
        riskLevel: signal.risk_level || ''
      }));
    }

    // Extract Internal Compliance status
    if (auditReport.controls?.internal_compliance) {
      const internalCompliance = auditReport.controls.internal_compliance;
      // Determine overall status based on individual compliance items
      const hasFailures = internalCompliance.some(
        item => item.status === 'FAIL' || item.status === 'NO_POLICY_CONFIGURED'
      );
      metrics.internalCompliance = hasFailures ? 'PARTIAL' : 'COMPLIANT';
    }

    // Extract External Compliance from structured data
    if (auditReport.controls?.external_compliance) {
      // Group by regulation and determine status
      // Use a Map to track the best status for each framework
      const frameworkMap = new Map<string, string>();
      auditReport.controls.external_compliance.forEach(compliance => {
        compliance.matches.forEach(match => {
          const framework = match.regulation;
          // If framework not seen, or if current status is better (COMPLIANT > PARTIAL > FAIL)
          if (!frameworkMap.has(framework)) {
            frameworkMap.set(
              framework,
              compliance.status === 'COMPLIANT'
                ? 'Compliant'
                : compliance.status === 'PARTIAL'
                ? 'Partial alignment'
                : 'Non-compliant'
            );
          } else {
            // Upgrade status if current is better
            const currentStatus = frameworkMap.get(framework) || '';
            if (compliance.status === 'COMPLIANT' && currentStatus !== 'Compliant') {
              frameworkMap.set(framework, 'Compliant');
            } else if (compliance.status === 'PARTIAL' && currentStatus === 'Non-compliant') {
              frameworkMap.set(framework, 'Partial alignment');
            }
          }
        });
      });
      metrics.externalCompliance = Array.from(frameworkMap.entries()).map(([framework, status]) => ({
        framework,
        status
      }));
    }

    return metrics;
  };

  const handleDownloadPDF = async () => {
    if (!reportRef.current || !auditReport) {
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'No report available to download.'
        })
      );
      return;
    }

    setIsDownloading(true);

    try {
      const element = reportRef.current;

      // Remove buttons and interactive elements before capturing
      const buttons = element.querySelectorAll('button');
      const buttonStates: Array<{ button: HTMLElement; display: string }> = [];
      buttons.forEach(btn => {
        const htmlBtn = btn as HTMLElement;
        buttonStates.push({
          button: htmlBtn,
          display: htmlBtn.style.display
        });
        htmlBtn.style.display = 'none';
      });

      // Configure html2canvas options
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight,
        onclone: clonedDoc => {
          // Helper function to format text in cloned document
          const formatTextForPDF = (text: string): string => {
            if (!text) return '';
            return text
              .replace(/_/g, ' ')
              .split(' ')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
          };

          // Ensure all styles are applied in the cloned document
          const clonedElement = clonedDoc.querySelector('[data-report-content]') as HTMLElement;
          if (clonedElement) {
            // Force print styles
            const style = clonedDoc.createElement('style');
            style.textContent = `
              * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              .pdf-only-divider {
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
              }
              .MuiChip-root {
                border: 1px solid #e0e0e0 !important;
              }
              .MuiChip-label {
                font-weight: 600 !important;
                opacity: 1 !important;
                visibility: visible !important;
                display: block !important;
                text-transform: none !important;
                letter-spacing: normal !important;
                white-space: nowrap !important;
              }
              .MuiChip-colorDefault .MuiChip-label {
                color: #000000 !important;
              }
              .MuiChip-colorSuccess .MuiChip-label,
              .MuiChip-colorError .MuiChip-label,
              .MuiChip-colorWarning .MuiChip-label {
                color: #ffffff !important;
              }
              .MuiTableCell-root {
                color: #000000 !important;
                border: 1px solid #ddd !important;
                background-color: #ffffff !important;
              }
              .MuiTableCell-root * {
                color: #000000 !important;
              }
              .MuiTypography-root {
                color: #000000 !important;
              }
              .MuiTableContainer-root,
              .MuiTableContainer-root table {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              table {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              thead {
                display: table-header-group !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              tfoot {
                display: table-footer-group !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              tbody {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              tr {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                page-break-after: auto !important;
                page-break-before: auto !important;
                display: table-row !important;
              }
              tbody tr {
                page-break-before: auto !important;
                page-break-after: auto !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                orphans: 3 !important;
                widows: 3 !important;
              }
              tbody tr:first-child {
                page-break-before: avoid !important;
              }
              td, th {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                display: table-cell !important;
              }
              .MuiTableRow-root {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                display: table-row !important;
              }
              .MuiTableBody-root .MuiTableRow-root {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                display: table-row !important;
              }
              .MuiTableHead-root .MuiTableRow-root {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
                display: table-row !important;
              }
              .MuiCard-root {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              td, th {
                color: #000000 !important;
                background-color: #ffffff !important;
              }
            `;
            clonedDoc.head.appendChild(style);

            // Hide buttons in cloned document
            const clonedButtons = clonedElement.querySelectorAll('button');
            clonedButtons.forEach(btn => {
              (btn as HTMLElement).style.display = 'none';
            });

            // Ensure all table cells have visible text
            const tableCells = clonedElement.querySelectorAll('td, th');
            tableCells.forEach(cell => {
              const htmlCell = cell as HTMLElement;
              htmlCell.style.color = '#000000';
              htmlCell.style.backgroundColor = '#ffffff';

              // Ensure all text inside cells is visible
              const textElements = htmlCell.querySelectorAll('*');
              textElements.forEach(el => {
                const htmlEl = el as HTMLElement;
                htmlEl.style.color = '#000000';
                htmlEl.style.opacity = '1';
                htmlEl.style.visibility = 'visible';

                // Force text nodes to be visible
                if (htmlEl.textContent && htmlEl.textContent.trim() !== '') {
                  htmlEl.style.display = '';
                }
              });

              // Ensure direct text nodes are visible
              const walker = clonedDoc.createTreeWalker(htmlCell, NodeFilter.SHOW_TEXT, null);
              let textNode;
              while ((textNode = walker.nextNode())) {
                if (textNode.textContent && textNode.textContent.trim() !== '') {
                  const parent = textNode.parentElement;
                  if (parent) {
                    parent.style.color = '#000000';
                    parent.style.opacity = '1';
                    parent.style.visibility = 'visible';
                  }
                }
              }
            });

            // Ensure all chips have visible text
            const chips = clonedElement.querySelectorAll('.MuiChip-root');
            chips.forEach(chip => {
              const htmlChip = chip as HTMLElement;
              // Keep original background color but ensure border
              htmlChip.style.border = '1px solid #e0e0e0';

              // Check if chip is default/grey color
              const isDefaultChip =
                htmlChip.classList.contains('MuiChip-colorDefault') ||
                (!htmlChip.classList.contains('MuiChip-colorSuccess') &&
                  !htmlChip.classList.contains('MuiChip-colorError') &&
                  !htmlChip.classList.contains('MuiChip-colorWarning'));

              const label = htmlChip.querySelector('.MuiChip-label');
              if (label) {
                const labelEl = label as HTMLElement;
                // Use black text for default/grey chips, white for colored chips
                labelEl.style.color = isDefaultChip ? '#000000' : '#ffffff';
                labelEl.style.fontWeight = '600';
                labelEl.style.opacity = '1';
                labelEl.style.visibility = 'visible';
                labelEl.style.display = 'block';
                labelEl.style.position = 'relative';
                labelEl.style.zIndex = '1';
                labelEl.style.textTransform = 'none';
                labelEl.style.letterSpacing = 'normal';
                labelEl.style.fontFamily = 'inherit';
                labelEl.style.whiteSpace = 'nowrap';
                // Remove text shadow to prevent blur/doubled appearance
                labelEl.style.textShadow = 'none';

                // Get text content - check multiple sources
                let chipText = labelEl.textContent || labelEl.innerText;

                // If label is empty, try to get text from parent cell
                if (!chipText || chipText.trim() === '') {
                  const parentCell = htmlChip.closest('td, th');
                  if (parentCell) {
                    const dataText = (parentCell as HTMLElement).getAttribute('data-chip-text');
                    if (dataText) {
                      chipText = dataText;
                    }
                  }
                }

                // Format and set the text content explicitly
                if (chipText && chipText.trim() !== '') {
                  const formattedText = formatTextForPDF(chipText.trim());
                  // Remove any existing backup spans to prevent duplication
                  const existingBackup = labelEl.querySelector('.backup-text');
                  if (existingBackup) {
                    existingBackup.remove();
                  }

                  // Set text content directly
                  labelEl.textContent = formattedText;
                  labelEl.innerText = formattedText;
                }
              }
            });

            // For cells with data-chip-text, ensure the text is visible
            const cellsWithChipText = clonedElement.querySelectorAll('[data-chip-text]');
            cellsWithChipText.forEach(cell => {
              const htmlCell = cell as HTMLElement;
              const chipText = htmlCell.getAttribute('data-chip-text');

              if (!chipText) return;

              // Check if chip text is visible
              const chip = htmlCell.querySelector('.MuiChip-root');
              if (chip) {
                const chipEl = chip as HTMLElement;
                let chipLabel = chipEl.querySelector('.MuiChip-label') as HTMLElement;

                if (!chipLabel) {
                  // Create label if it doesn't exist
                  chipLabel = clonedDoc.createElement('span');
                  chipLabel.className = 'MuiChip-label';
                  chipEl.appendChild(chipLabel);
                }

                // Format and set text content
                const formattedText = formatTextForPDF(chipText);
                chipLabel.textContent = formattedText;
                chipLabel.innerText = formattedText;

                // Check if chip is default/grey color
                const isDefaultChip =
                  chipEl.classList.contains('MuiChip-colorDefault') ||
                  (!chipEl.classList.contains('MuiChip-colorSuccess') &&
                    !chipEl.classList.contains('MuiChip-colorError') &&
                    !chipEl.classList.contains('MuiChip-colorWarning'));

                chipLabel.style.color = isDefaultChip ? '#000000' : '#ffffff';
                chipLabel.style.fontWeight = '600';
                chipLabel.style.opacity = '1';
                chipLabel.style.visibility = 'visible';
                chipLabel.style.display = 'block';
                chipLabel.style.position = 'relative';
                chipLabel.style.zIndex = '10';
                chipLabel.style.textTransform = 'none';
                chipLabel.style.letterSpacing = 'normal';
                chipLabel.style.fontFamily = 'inherit';
                chipLabel.style.whiteSpace = 'nowrap';
                chipLabel.style.textShadow = 'none';

                // Also ensure chip itself is visible
                chipEl.style.display = 'inline-flex';
                chipEl.style.visibility = 'visible';
                chipEl.style.opacity = '1';
              }
            });

            // Ensure all Typography elements have visible text
            const typographyElements = clonedElement.querySelectorAll('.MuiTypography-root');
            typographyElements.forEach(typography => {
              const htmlTypo = typography as HTMLElement;
              htmlTypo.style.color = '#000000';
            });

            // Add data attributes to table rows for page break detection
            const tableRows = clonedElement.querySelectorAll('tr, .MuiTableRow-root');
            tableRows.forEach((row, index) => {
              const htmlRow = row as HTMLElement;
              htmlRow.setAttribute('data-row-index', index.toString());
              htmlRow.style.pageBreakInside = 'avoid';
              htmlRow.style.breakInside = 'avoid';
            });
          }
        }
      });

      // Restore buttons
      buttonStates.forEach(({ button, display }) => {
        button.style.display = display;
      });

      // Calculate PDF dimensions (A4: 210mm x 297mm)
      const pdfWidth = 210; // A4 width in mm
      const pdfHeight = 297; // A4 height in mm
      const margin = 10; // 10mm margin on all sides
      const imgWidth = pdfWidth - 2 * margin;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageHeight = pdfHeight - 2 * margin; // Usable height per page

      // Get table row positions from the original element for smart page breaks
      // Include all possible row selectors to catch all table rows
      const tableRows = element.querySelectorAll(
        'tr, .MuiTableRow-root, table tr, .MuiTableContainer-root tr, tbody tr, thead tr'
      );
      const rowPositions: Array<{
        top: number;
        bottom: number;
        isHeader: boolean;
      }> = [];
      const elementRect = element.getBoundingClientRect();

      tableRows.forEach(row => {
        const rect = row.getBoundingClientRect();
        // Only process rows that are actually visible and have height
        if (rect.height > 0) {
          const relativeTop = (rect.top - elementRect.top) * (canvas.height / element.scrollHeight);
          const relativeBottom = (rect.bottom - elementRect.top) * (canvas.height / element.scrollHeight);
          const isHeader =
            row.tagName === 'THEAD' ||
            row.closest('thead') !== null ||
            row.closest('.MuiTableHead-root') !== null ||
            row.classList.contains('MuiTableHead-root') ||
            row.querySelector('th') !== null ||
            (row as HTMLElement).parentElement?.classList.contains('MuiTableHead-root') === true;
          rowPositions.push({
            top: relativeTop,
            bottom: relativeBottom,
            isHeader
          });
        }
      });

      // Sort by top position to ensure correct order
      rowPositions.sort((a, b) => a.top - b.top);

      // Also get table container positions to avoid breaking tables
      const tableContainers = element.querySelectorAll('.MuiTableContainer-root, .MuiCard-root');
      const containerPositions: Array<{ top: number; bottom: number }> = [];
      tableContainers.forEach(container => {
        const rect = container.getBoundingClientRect();
        const relativeTop = (rect.top - elementRect.top) * (canvas.height / element.scrollHeight);
        const relativeBottom = (rect.bottom - elementRect.top) * (canvas.height / element.scrollHeight);
        containerPositions.push({ top: relativeTop, bottom: relativeBottom });
      });

      // Calculate how many pages we need
      const pixelsPerMm = canvas.height / imgHeight;
      const pageHeightPixels = pageHeight * pixelsPerMm;

      // Split canvas into pages with smart row-aware breaks
      let currentY = 0;
      let page = 0;

      while (currentY < canvas.height) {
        if (page > 0) {
          pdf.addPage();
        }

        // Calculate where this page should end
        let pageEndY = currentY + pageHeightPixels;

        // Check if we're cutting through a table row
        // NEVER split a row - if any part would be cut, move entire row to next page
        // But be smarter to fill pages better
        if (rowPositions.length > 0) {
          // Find rows that would be cut by this page break
          const rowsToCheck = rowPositions.filter(row => row.top < pageEndY && row.bottom > pageEndY);

          if (rowsToCheck.length > 0) {
            // Find the first row that would be cut
            const cutRow = rowsToCheck[0];
            const rowHeight = cutRow.bottom - cutRow.top;
            const rowHeightOnPage = pageEndY - cutRow.top;

            // Calculate how much of the page is already filled
            const currentPageHeight = pageEndY - currentY;
            const pageFillRatio = currentPageHeight / pageHeightPixels;

            // Always prevent row breaks, but be smart about when to move
            // If row would be significantly cut (less than 90% fits) OR page is already well-filled (>= 70%)
            if (rowHeightOnPage < rowHeight * 0.9 || pageFillRatio >= 0.7) {
              // Move page end to just before this row starts
              // But ensure we don't make the page too short (at least 40% filled)
              const minPageHeight = pageHeightPixels * 0.4;
              const newPageEnd = Math.max(cutRow.top - 3, currentY + minPageHeight);

              // Only adjust if we're not making the page too short
              if (newPageEnd >= currentY + minPageHeight) {
                pageEndY = newPageEnd;
              }
            }
          }
        }

        // Check if we're cutting through a table container
        // Only move if container would be awkwardly split AND page is well-filled
        if (containerPositions.length > 0) {
          const containersToCheck = containerPositions.filter(
            container => container.top < pageEndY && container.bottom > pageEndY
          );

          if (containersToCheck.length > 0) {
            const cutContainer = containersToCheck[0];
            const containerHeightOnPage = pageEndY - cutContainer.top;
            const containerHeight = cutContainer.bottom - cutContainer.top;
            const currentPageHeight = pageEndY - currentY;
            const pageFillRatio = currentPageHeight / pageHeightPixels;

            // Only move if:
            // 1. Less than 40% of container fits on current page
            // 2. Container is reasonably large (at least 15% of page height)
            // 3. Current page is already well-filled (>= 60%)
            if (
              containerHeightOnPage < containerHeight * 0.4 &&
              containerHeight > pageHeightPixels * 0.15 &&
              pageFillRatio >= 0.6
            ) {
              const minPageHeight = pageHeightPixels * 0.4;
              const newPageEnd = Math.max(cutContainer.top - 3, currentY + minPageHeight);

              if (newPageEnd >= currentY + minPageHeight) {
                pageEndY = newPageEnd;
              }
            }
          }
        }

        // Ensure we don't exceed canvas height
        pageEndY = Math.min(pageEndY, canvas.height);
        const sourceY = currentY;
        const sourceHeight = pageEndY - currentY;

        if (sourceHeight <= 0) break;

        // Create a temporary canvas for this page
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sourceHeight;
        const pageCtx = pageCanvas.getContext('2d');

        if (pageCtx) {
          // Draw the portion of the image for this page
          pageCtx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);

          // Calculate the height of this page's image in mm
          const pageImgHeight = (sourceHeight * imgWidth) / canvas.width;

          // Add to PDF
          pdf.addImage(pageCanvas.toDataURL('image/png', 1.0), 'PNG', margin, margin, imgWidth, pageImgHeight);
        }

        // Move to next page start
        currentY = pageEndY;
        page++;

        // Safety check to prevent infinite loop
        if (page > 100) break;
      }

      // Save PDF
      const filename = `Audit_Report_${projectId}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

      dispatch(
        setToast({
          show: true,
          title: 'Success',
          message: 'Audit report downloaded successfully.'
        })
      );
    } catch (err: any) {
      console.error('Error generating PDF:', err);
      dispatch(
        setToast({
          show: true,
          title: 'Error',
          message: 'Failed to generate PDF. Please try again.'
        })
      );
    } finally {
      setIsDownloading(false);
    }
  };

  // Helper function to format text properly
  const formatStatusText = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Parse full_text_report into table format
  const parseFullTextReport = (reportText: string) => {
    if (!reportText) return [];

    const rows: Array<{ section: string; key: string; value: string }> = [];
    const lines = reportText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    let currentSection = '';
    let currentKey = '';
    let currentValue = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Skip separator lines (lines with only dashes, equals, or underscores)
      if (/^[-=_]+$/.test(line)) {
        continue;
      }

      // Detect section headers (lines starting with numbers followed by text, or all caps with separators)
      const sectionMatch = line.match(/^(\d+\.?\s+)?([A-Z][A-Z\s&-]+)$/);
      if (sectionMatch && (line.length > 15 || /^\d+\./.test(line))) {
        // Save previous entry if exists
        if (currentKey && currentValue) {
          rows.push({
            section: currentSection,
            key: currentKey,
            value: currentValue.trim()
          });
          currentKey = '';
          currentValue = '';
        }
        currentSection = line;
        continue;
      }

      // Detect key-value pairs (format: "Key : Value" or "Key: Value")
      const keyValueMatch = line.match(/^([^:]+?)\s*[:]\s*(.+)$/);
      if (keyValueMatch) {
        // Save previous entry if exists
        if (currentKey && currentValue) {
          rows.push({
            section: currentSection,
            key: currentKey,
            value: currentValue.trim()
          });
        }
        currentKey = keyValueMatch[1].trim();
        currentValue = keyValueMatch[2].trim();
        continue;
      }

      // Detect list items (lines starting with "-", "•", "*", or numbered)
      if (/^[-•*]\s/.test(line) || /^\d+[.)]\s/.test(line)) {
        if (currentKey && currentKey !== 'List Item') {
          // Save current entry and start new list
          rows.push({
            section: currentSection,
            key: currentKey,
            value: currentValue.trim()
          });
          currentKey = 'List Item';
          currentValue = line.replace(/^[-•*]\s*/, '').replace(/^\d+[.)]\s*/, '');
        } else {
          // Append to current list
          const listItem = line.replace(/^[-•*]\s*/, '').replace(/^\d+[.)]\s*/, '');
          currentValue += (currentValue ? ' | ' : '') + listItem;
          if (!currentKey) {
            currentKey = 'List Item';
          }
        }
        continue;
      }

      // Detect table-like rows (lines with multiple "|" separators)
      if (line.includes('|') && line.split('|').length > 2) {
        const parts = line
          .split('|')
          .map(p => p.trim())
          .filter(p => p.length > 0);
        if (parts.length >= 2) {
          if (currentKey && currentValue) {
            rows.push({
              section: currentSection,
              key: currentKey,
              value: currentValue.trim()
            });
          }
          currentKey = parts[0];
          currentValue = parts.slice(1).join(' | ');
          continue;
        }
      }

      // If line doesn't match any pattern, append to current value or create new entry
      if (currentKey) {
        // Append to current value if it's a continuation
        if (currentValue && !currentValue.endsWith('.') && !currentValue.endsWith(':')) {
          currentValue += ' ' + line;
        } else {
          currentValue = line;
        }
      } else if (line.length > 0) {
        // If no current key, treat as a standalone value
        rows.push({
          section: currentSection,
          key: 'Content',
          value: line
        });
      }
    }

    // Save last entry if exists
    if (currentKey && currentValue) {
      rows.push({
        section: currentSection,
        key: currentKey,
        value: currentValue.trim()
      });
    }

    return rows;
  };

  const metrics = auditReport ? extractMetrics(auditReport) : {};

  // Calculate summary stats
  const summaryStats = {
    totalOwaspTests: metrics.owasp?.length || 0,
    passedOwaspTests: metrics.owasp?.filter((m: any) => m.status === 'PASS').length || 0,
    failedOwaspTests: metrics.owasp?.filter((m: any) => m.status === 'FAIL').length || 0,
    highRiskAreas: metrics.riskLevels?.filter((r: any) => r.riskLevel === 'HIGH').length || 0,
    compliantFrameworks:
      metrics.externalCompliance?.filter((e: any) => e.status.toLowerCase().includes('compliant')).length || 0
  };

  return (
    <Box sx={{ padding: '24px 32px' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          {auditReport && (
            <Tooltip title={isDownloading ? 'Downloading...' : 'Download PDF'}>
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'primary.main',
                  borderRadius: 2,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'background.paper',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderColor: 'primary.dark'
                  }
                }}
              >
                <IconButton
                  onClick={handleDownloadPDF}
                  disabled={isDownloading || !auditReport}
                  color="primary"
                  size="small"
                  sx={{
                    p: 1.5
                  }}
                >
                  {isDownloading ? <CircularProgress size={20} /> : <FontAwesomeIcon icon={faCloudDownload} />}
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!auditReport && (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" align="center">
              No audit report generated yet. Click "Generate Audit Report" to create a new report.
            </Typography>
          </CardContent>
        </Card>
      )}

      {auditReport && (
        <Box
          ref={reportRef}
          data-report-content
          sx={{
            backgroundColor: '#ffffff',
            padding: '20px',
            minHeight: '100%',
            width: '100%',
            display: 'block',
            visibility: 'visible',
            opacity: 1
          }}
        >
          {/* Report Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
              pb: 2,
              borderBottom: '2px solid #e0e0e0',
              pageBreakInside: 'avoid',
              breakInside: 'avoid'
            }}
          >
            {/* Left Side - Project Information */}
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#1a1a1a',
                  mb: 3,
                  fontSize: '1.75rem'
                }}
              >
                AI Ethics and Compliance Audit Report
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <FontAwesomeIcon icon={faGlobe} style={{ color: '#666', fontSize: '16px' }} />
                  <Typography variant="body1" sx={{ color: '#333', fontSize: '0.95rem' }}>
                    {auditReport.project_name || 'N/A'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <FontAwesomeIcon icon={faUser} style={{ color: '#666', fontSize: '16px' }} />
                  <Typography variant="body1" sx={{ color: '#333', fontSize: '0.95rem' }}>
                    Reviewer : {auditReport.reviewer.name || 'System'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <FontAwesomeIcon icon={faCalendar} style={{ color: '#666', fontSize: '16px' }} />
                  <Typography variant="body1" sx={{ color: '#333', fontSize: '0.95rem' }}>
                    Audit Date :{' '}
                    {new Date(auditReport.audit_timestamp_local).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit'
                    })}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mt: 1
                  }}
                >
                  <FontAwesomeIcon icon={faFolder} style={{ color: '#999', fontSize: '14px' }} />
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                    Project Id : {auditReport.project_id}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Side - Logo */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                ml: 4
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end'
                }}
              >
                <img
                  src={logo}
                  alt="MICROeAI Logo"
                  style={{
                    width: '120px',
                    height: 'auto'
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Overall Status
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    <Chip
                      label={formatStatusText(auditReport.reviewer.verdict)}
                      color={
                        auditReport.reviewer.verdict === 'APPROVE' || auditReport.reviewer.verdict === 'APPROVED'
                          ? 'success'
                          : auditReport.reviewer.verdict === 'UNDER_REVIEW' || auditReport.reviewer.verdict === 'REVIEW'
                          ? 'warning'
                          : 'error'
                      }
                      sx={{
                        fontSize: '1rem',
                        height: 'auto',
                        py: 1,
                        '& .MuiChip-label': {
                          color: '#ffffff',
                          fontWeight: 600,
                          textTransform: 'none',
                          letterSpacing: 'normal',
                          fontFamily: 'inherit',
                          whiteSpace: 'nowrap',
                          textShadow: 'none'
                        }
                      }}
                    />
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    OWASP Tests Passed
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {summaryStats.passedOwaspTests} / {summaryStats.totalOwaspTests}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    High Risk Areas
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {summaryStats.highRiskAreas}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Compliant Frameworks
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {summaryStats.compliantFrameworks}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Separator after Summary Cards */}
          {(auditReport.kpis_dimension_level ||
            auditReport.scope_of_analysis ||
            metrics.owasp ||
            metrics.riskLevels ||
            auditReport.controls?.internal_compliance ||
            auditReport.controls?.external_compliance ||
            auditReport.risk_and_recommendations_advisory ||
            auditReport.final_verdict_reviewer_only ||
            auditReport.full_text_report ||
            auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Ethical Behavior Score
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {auditReport.kpis_dimension_level.ethical_behavior_score.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    AI Safety Risk
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {auditReport.kpis_dimension_level.ai_safety_risk_pct.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Governance Coverage
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                    {auditReport.kpis_dimension_level.governance_coverage_pct.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Internal Violations
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {auditReport.kpis_dimension_level.internal_violations_count}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    External Violations
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                    {auditReport.kpis_dimension_level.external_violations_count}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Separator after KPI Cards */}
          {(auditReport.scope_of_analysis ||
            metrics.owasp ||
            metrics.riskLevels ||
            auditReport.controls?.internal_compliance ||
            auditReport.controls?.external_compliance ||
            auditReport.risk_and_recommendations_advisory ||
            auditReport.final_verdict_reviewer_only ||
            auditReport.full_text_report ||
            auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* Scope of Analysis */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Scope of Analysis
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    ML Units Evaluated
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {auditReport.scope_of_analysis.ml_units_evaluated}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    LLM Units Evaluated
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {auditReport.scope_of_analysis.llm_units_evaluated}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Separator after Scope of Analysis */}
          {(metrics.owasp ||
            metrics.riskLevels ||
            auditReport.controls?.internal_compliance ||
            auditReport.controls?.external_compliance ||
            auditReport.risk_and_recommendations_advisory ||
            auditReport.final_verdict_reviewer_only ||
            auditReport.full_text_report ||
            auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* OWASP Tests Table */}
          {metrics.owasp && metrics.owasp.length > 0 && (
            <Card sx={{ mb: 2, pageBreakInside: 'avoid' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  OWASP AI Security & Safety Controls
                </Typography>
                <TableContainer>
                  <Table
                    sx={{
                      '& .MuiTableRow-root': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid'
                      },
                      '& .MuiTableCell-root': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        fontSize: '0.75rem',
                        padding: '6px 8px'
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Test</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Severity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics.owasp.map((test: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell sx={{ color: '#000000', fontSize: '0.75rem' }}>{test.test}</TableCell>
                          <TableCell sx={{ color: '#000000', fontSize: '0.75rem' }} data-chip-text={test.status}>
                            <Chip
                              label={test.status}
                              color={test.status === 'PASS' ? 'success' : test.status === 'FAIL' ? 'error' : 'warning'}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                '& .MuiChip-label': {
                                  color: '#ffffff',
                                  fontSize: '0.7rem',
                                  padding: '0 6px',
                                  fontWeight: 600
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#000000', fontSize: '0.75rem' }} data-chip-text={test.severity}>
                            <Chip
                              label={test.severity}
                              color={test.severity === 'High' ? 'error' : 'default'}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                '& .MuiChip-label': {
                                  color: test.severity === 'High' ? '#ffffff' : '#000000',
                                  fontSize: '0.7rem',
                                  padding: '0 6px',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  letterSpacing: 'normal',
                                  fontFamily: 'inherit',
                                  whiteSpace: 'nowrap',
                                  textShadow: 'none'
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Separator after OWASP Tests Table */}
          {(metrics.riskLevels ||
            auditReport.controls?.internal_compliance ||
            auditReport.controls?.external_compliance ||
            auditReport.risk_and_recommendations_advisory ||
            auditReport.final_verdict_reviewer_only ||
            auditReport.full_text_report ||
            auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* Risk Levels Table */}
          {metrics.riskLevels && metrics.riskLevels.length > 0 && (
            <Card sx={{ mb: 2, pageBreakInside: 'avoid' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Ontology-Based Governance Signals
                </Typography>
                <TableContainer>
                  <Table
                    sx={{
                      '& .MuiTableRow-root': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid'
                      },
                      '& .MuiTableCell-root': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        fontSize: '0.75rem',
                        padding: '6px 8px'
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Risk Level</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics.riskLevels.map((risk: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell sx={{ color: '#000000', fontSize: '0.75rem' }}>{risk.category}</TableCell>
                          <TableCell sx={{ color: '#000000', fontSize: '0.75rem' }} data-chip-text={risk.riskLevel}>
                            <Chip
                              label={risk.riskLevel}
                              color={
                                risk.riskLevel === 'HIGH' ? 'error' : risk.riskLevel === 'LOW' ? 'success' : 'default'
                              }
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                '& .MuiChip-label': {
                                  color: risk.riskLevel === 'HIGH' || risk.riskLevel === 'LOW' ? '#ffffff' : '#000000',
                                  fontSize: '0.7rem',
                                  padding: '0 6px',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  letterSpacing: 'normal',
                                  fontFamily: 'inherit',
                                  whiteSpace: 'nowrap',
                                  textShadow: 'none'
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Separator after Risk Levels Table */}
          {(auditReport.controls?.internal_compliance ||
            auditReport.controls?.external_compliance ||
            auditReport.risk_and_recommendations_advisory ||
            auditReport.final_verdict_reviewer_only ||
            auditReport.full_text_report ||
            auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* Internal Compliance Table */}
          {auditReport.controls?.internal_compliance && auditReport.controls.internal_compliance.length > 0 && (
            <Card sx={{ mb: 2, pageBreakInside: 'avoid' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Internal Compliance Assessment
                </Typography>
                <TableContainer>
                  <Table
                    sx={{
                      '& .MuiTableRow-root': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid'
                      },
                      '& .MuiTableCell-root': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        fontSize: '0.75rem',
                        padding: '6px 8px'
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Dimension</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Issue Type</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Severity</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditReport.controls.internal_compliance.map((item: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{item.dimension}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>{item.issue_type}</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }} data-chip-text={item.severity}>
                            <Chip
                              label={item.severity}
                              color={item.severity === 'HIGH' ? 'error' : 'default'}
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                '& .MuiChip-label': {
                                  color: item.severity === 'HIGH' ? '#ffffff' : '#000000',
                                  fontSize: '0.7rem',
                                  padding: '0 6px',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  letterSpacing: 'normal',
                                  fontFamily: 'inherit',
                                  whiteSpace: 'nowrap',
                                  textShadow: 'none'
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }} data-chip-text={item.status}>
                            <Chip
                              label={item.status}
                              color={
                                item.status === 'COMPLIANT'
                                  ? 'success'
                                  : item.status === 'PARTIAL'
                                  ? 'warning'
                                  : 'error'
                              }
                              size="small"
                              sx={{
                                fontSize: '0.7rem',
                                height: '20px',
                                '& .MuiChip-label': {
                                  color: '#000000',
                                  fontSize: '0.7rem',
                                  padding: '0 6px'
                                }
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Separator after Internal Compliance Table */}
          {(auditReport.controls?.external_compliance ||
            auditReport.risk_and_recommendations_advisory ||
            auditReport.final_verdict_reviewer_only ||
            auditReport.full_text_report ||
            auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* External Compliance Table */}
          {auditReport.controls?.external_compliance && auditReport.controls.external_compliance.length > 0 && (
            <Card sx={{ mb: 2, pageBreakInside: 'avoid' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  External Regulatory Alignment
                </Typography>
                <TableContainer>
                  <Table
                    sx={{
                      '& .MuiTableRow-root': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid'
                      },
                      '& .MuiTableCell-root': {
                        pageBreakInside: 'avoid',
                        breakInside: 'avoid',
                        fontSize: '0.75rem',
                        padding: '6px 8px'
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Dimension</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Regulation</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Article</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditReport.controls.external_compliance.map((compliance: any, index: number) =>
                        compliance.matches.map((match: any, matchIndex: number) => (
                          <TableRow key={`${index}-${matchIndex}`}>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{compliance.dimension}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{match.regulation}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }}>{match.article}</TableCell>
                            <TableCell sx={{ fontSize: '0.75rem' }} data-chip-text={compliance.status}>
                              <Chip
                                label={compliance.status}
                                color={
                                  compliance.status === 'COMPLIANT'
                                    ? 'success'
                                    : compliance.status === 'PARTIAL'
                                    ? 'warning'
                                    : 'error'
                                }
                                size="small"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: '20px',
                                  '& .MuiChip-label': {
                                    color: '#000000',
                                    fontSize: '0.7rem',
                                    padding: '0 6px'
                                  }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Separator after External Compliance Table */}
          {(auditReport.risk_and_recommendations_advisory ||
            auditReport.final_verdict_reviewer_only ||
            auditReport.full_text_report ||
            auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* Risk and Recommendations Advisory */}
          {auditReport.risk_and_recommendations_advisory && (
            <Card sx={{ mb: 2, pageBreakInside: 'avoid' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Risk and Recommendations Advisory
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6
                  }}
                >
                  {auditReport.risk_and_recommendations_advisory}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Separator after Risk and Recommendations Advisory */}
          {(auditReport.final_verdict_reviewer_only || auditReport.full_text_report || auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* Final Verdict */}
          {auditReport.final_verdict_reviewer_only && (
            <Card sx={{ mb: 2, pageBreakInside: 'avoid' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Final Verdict (Reviewer Only)
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ mb: 1 }}>
                  <Chip
                    label={formatStatusText(auditReport.final_verdict_reviewer_only.verdict)}
                    color={
                      auditReport.final_verdict_reviewer_only.verdict === 'APPROVE' ||
                      auditReport.final_verdict_reviewer_only.verdict === 'APPROVED'
                        ? 'success'
                        : auditReport.final_verdict_reviewer_only.verdict === 'UNDER_REVIEW' ||
                          auditReport.final_verdict_reviewer_only.verdict === 'REVIEW'
                        ? 'warning'
                        : 'error'
                    }
                    sx={{
                      fontSize: '1rem',
                      height: 'auto',
                      py: 1,
                      mb: 2,
                      '& .MuiChip-label': {
                        color: '#ffffff',
                        fontWeight: 600,
                        textTransform: 'none',
                        letterSpacing: 'normal',
                        fontFamily: 'inherit',
                        whiteSpace: 'nowrap'
                      }
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6
                  }}
                >
                  {auditReport.final_verdict_reviewer_only.rationale_verbatim}
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Separator after Final Verdict */}
          {(auditReport.full_text_report || auditReport.ocaf_anchor) && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* Full Report Display */}
          {auditReport.full_text_report && (
            <Card sx={{ mb: 2, pageBreakInside: 'avoid' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Full Audit Report
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {(() => {
                  const parsedReport = parseFullTextReport(auditReport.full_text_report);

                  if (parsedReport.length === 0) {
                    // Fallback to raw text if parsing fails
                    return (
                      <Box
                        sx={{
                          backgroundColor: '#f5f5f5',
                          padding: 3,
                          borderRadius: 2,
                          maxHeight: '600px',
                          overflow: 'auto',
                          fontFamily: 'monospace',
                          fontSize: '0.875rem',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {auditReport.full_text_report}
                      </Box>
                    );
                  }

                  // Group by section
                  const sections = parsedReport.reduce(
                    (acc, row) => {
                      const section = row.section || 'General Information';
                      if (!acc[section]) {
                        acc[section] = [];
                      }
                      acc[section].push(row);
                      return acc;
                    },
                    {} as Record<string, typeof parsedReport>
                  );

                  return (
                    <Box>
                      {Object.entries(sections).map(([section, rows], sectionIndex) => (
                        <Box
                          key={sectionIndex}
                          sx={{
                            mb: sectionIndex < Object.keys(sections).length - 1 ? 2 : 0
                          }}
                        >
                          {section && (
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                mb: 1,
                                mt: sectionIndex > 0 ? 1.5 : 0,
                                color: '#333'
                              }}
                            >
                              {section}
                            </Typography>
                          )}
                          <TableContainer>
                            <Table
                              sx={{
                                '& .MuiTableRow-root': {
                                  pageBreakInside: 'avoid',
                                  breakInside: 'avoid'
                                },
                                '& .MuiTableCell-root': {
                                  pageBreakInside: 'avoid',
                                  breakInside: 'avoid',
                                  fontSize: '0.75rem',
                                  padding: '8px 12px',
                                  borderBottom: '1px solid #e0e0e0'
                                }
                              }}
                            >
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.8rem',
                                      backgroundColor: '#f5f5f5'
                                    }}
                                  >
                                    Key
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: '0.8rem',
                                      backgroundColor: '#f5f5f5'
                                    }}
                                  >
                                    Value
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {rows.map((row, rowIndex) => (
                                  <TableRow key={rowIndex}>
                                    <TableCell
                                      sx={{
                                        color: '#000000',
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        width: '30%'
                                      }}
                                    >
                                      {row.key}
                                    </TableCell>
                                    <TableCell
                                      sx={{
                                        color: '#000000',
                                        fontSize: '0.75rem',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {row.value}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Separator after Full Audit Report */}
          {auditReport.ocaf_anchor?.ocaf_sha256_hash && (
            <Divider
              className="pdf-only-divider"
              sx={{
                my: 2,
                borderWidth: 1,
                borderColor: '#e0e0e0',
                display: 'none'
              }}
            />
          )}

          {/* Cryptographic Signature */}
          {auditReport.ocaf_anchor?.ocaf_sha256_hash && (
            <Card sx={{ mb: 2, pageBreakInside: 'avoid' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Cryptographic Signature
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {auditReport.ocaf_anchor.status && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        Status:
                      </Typography>
                      <Chip
                        label={formatStatusText(auditReport.ocaf_anchor.status)}
                        color={
                          auditReport.ocaf_anchor.status === 'VALID' || auditReport.ocaf_anchor.status === 'VERIFIED'
                            ? 'success'
                            : auditReport.ocaf_anchor.status === 'PENDING'
                            ? 'warning'
                            : auditReport.ocaf_anchor.status === 'LOCKED'
                            ? 'default'
                            : 'error'
                        }
                        sx={{
                          fontSize: '0.875rem',
                          height: 'auto',
                          py: 0.5,
                          '& .MuiChip-label': {
                            color:
                              auditReport.ocaf_anchor.status === 'LOCKED' ||
                              auditReport.ocaf_anchor.status === 'UNLOCKED'
                                ? '#000000'
                                : '#ffffff',
                            fontWeight: 600,
                            textTransform: 'none',
                            letterSpacing: 'normal',
                            fontFamily: 'inherit',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      backgroundColor: '#f5f5f5',
                      padding: 2,
                      borderRadius: 1,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      wordBreak: 'break-all'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                      OCAF SHA256 Hash:
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#000000' }}>
                      {auditReport.ocaf_anchor.ocaf_sha256_hash}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AuditReportTab;
